import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler, getStorageItem, removeStorageItem, saveStorageItem } from 'common/helpers/axios.helper';
import { EStorageKey } from 'common/const';
import { AppDispatch, RootState, store } from 'app/store';
import {
  IAuthLoginPayload,
  IAuthSendOtpPayload,
  IAuthSendPhoneOtpPayload,
  IAuthVerifyOtpPayload,
  IAuthVerifyPhoneOtpPayload,
  ILoginAsUserPayload,
  IRestorePasswordEmailPayload,
  IRestorePasswordPayload,
  IToken,
} from 'entities/Auth/Auth.models';
import { authTransport } from 'entities/Auth/Auth.transport';
import { clearCreds, saveCreds } from 'entities/Auth/Auth.helper';

interface IState {
  auth: IToken | null;
  isAuthLoading: boolean;
  authError: string | null;
  xUserId: number | null;
  xBasketId: number | null;
  userCatalogId: number | null;
}

const initialState: IState = {
  auth: null,
  isAuthLoading: true,
  authError: null,
  xUserId: null,
  xBasketId: null,
  userCatalogId: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth(state, action: PayloadAction<IToken | null>) {
      state.auth = action.payload;
    },
    setIsAuthLoading(state, action: PayloadAction<boolean>) {
      state.isAuthLoading = action.payload;
    },
    setAuthError(state, action: PayloadAction<string | null>) {
      state.authError = action.payload;
    },
    setXUserId(state, action: PayloadAction<number | null>) {
      state.xUserId = action.payload;
    },
    setXBasketId(state, action: PayloadAction<number | null>) {
      state.xBasketId = action.payload;
    },
    setUserCatalogId(state, action: PayloadAction<number | null>) {
      state.userCatalogId = action.payload;
    },
  },
});

export const { setAuth, setIsAuthLoading, setAuthError, setXUserId, setXBasketId, setUserCatalogId } = authSlice.actions;

export const selectAuth = (state: RootState) => state.auth.auth;
export const selectIsAuthLoading = (state: RootState) => state.auth.isAuthLoading;
export const selectAuthError = (state: RootState) => state.auth.authError;
export const selectXUserId = (state: RootState) => state.auth.xUserId;
export const selectXBasketId = (state: RootState) => state.auth.xBasketId;
export const selectUserCatalogId = (state: RootState) => state.auth.userCatalogId;

export const authReducer = authSlice.reducer;

export const initAuth = () => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  const creds = getStorageItem<IToken>(EStorageKey.Creds);
  const xUserId = getStorageItem<number>(EStorageKey.XUserId);
  const xBasketId = getStorageItem<number>(EStorageKey.XBasketId);
  const userCatalogId = getStorageItem<number>(EStorageKey.UserCatalogId);

  if (creds) {
    const now = Math.round(Date.now() / 1000);

    if (creds.refresh.expiredAt && creds.refresh.expiredAt < now) {
      clearCreds();
      return;
    }

    if (creds.access.expiredAt && creds.access.expiredAt < now) {
      if (creds.refresh.token) {
        try {
          const refreshResponse = await authTransport.refreshToken(creds.refresh.token);
          await saveCreds(refreshResponse);
        } catch (refreshError) {
          axiosErrorHandler(refreshError);
          clearCreds();
        }
      } else {
        clearCreds();
      }
    }

    dispatch(setAuth(creds));
    dispatch(setXUserId(xUserId));
    dispatch(setXBasketId(xBasketId));
    dispatch(setUserCatalogId(userCatalogId));
    dispatch(setIsAuthLoading(false));
  } else {
    clearCreds();
  }
};

export const addAuth = (payload: IAuthLoginPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    const response = await authTransport.addAuth(payload);
    await saveCreds(response);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const sendEmail = (payload: IAuthSendOtpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    await authTransport.sendEmail(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAuthError);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const verifyEmail = (payload: IAuthVerifyOtpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    const response = await authTransport.verifyEmail(payload);
    await saveCreds(response);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAuthError);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const sendPhone = (payload: IAuthSendPhoneOtpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    await authTransport.sendPhone(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAuthError);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const verifyPhone = (payload: IAuthVerifyPhoneOtpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    const response = await authTransport.verifyPhone(payload);
    await saveCreds(response);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAuthError);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const deleteAuth = () => async () => {
  try {
    await authTransport.deleteAuth();
  } finally {
    clearCreds();
  }
};

export const sendRestorePasswordEmail = (payload: IRestorePasswordEmailPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    await authTransport.sendRestorePasswordEmail(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setAuthError);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const restorePassword = (payload: IRestorePasswordPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsAuthLoading(true));

  try {
    const response = await authTransport.restorePassword(payload);
    await saveCreds(response);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsAuthLoading(false));
  }
};

export const loginAsUser = (payload: ILoginAsUserPayload) => async (dispatch: AppDispatch) => {
  const { user, isSeller } = payload;
  const state = store.getState();
  const auth = state.auth.auth;

  if (auth) {
    /*
      Access/Refresh token, issuedAt, expiredAt - from super admin creds
      */
    const creds = {
      access: {
        ...auth.access,
        id: user.id,
        userId: user.id,
        accountId: user.accountId,
        accountName: user.account.name,
        firstName: user.firstName,
        lastName: user.lastName,
        patronymic: user.patronymic,
        roles: user.roles,
        isSeller,
        basketId: user.basketId,
        permissions: user.permissions,
      },
      refresh: {
        ...auth.refresh,
        id: user.id,
        userId: user.id,
      },
    };

    dispatch(setAuth(creds));
    dispatch(setXUserId(user.id));
    saveStorageItem(EStorageKey.Creds, creds);
    saveStorageItem(EStorageKey.XUserId, user.id);
    saveStorageItem(EStorageKey.SuperAdminCreds, auth);
  }
};

export const logoutAsUser = () => async (dispatch: AppDispatch) => {
  const superAdminCreds = getStorageItem<IToken>(EStorageKey.SuperAdminCreds);

  if (superAdminCreds) {
    dispatch(setAuth(superAdminCreds));
    dispatch(setXUserId(null));
    dispatch(setXBasketId(null));
    dispatch(setUserCatalogId(null));
    saveStorageItem(EStorageKey.Creds, superAdminCreds);
    removeStorageItem(EStorageKey.XUserId);
    removeStorageItem(EStorageKey.XBasketId);
    removeStorageItem(EStorageKey.UserCatalogId);
    removeStorageItem(EStorageKey.SuperAdminCreds);
  } else {
    clearCreds();
  }
};
